import get from 'lodash/get';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import DataTable from 'components/DataTable';
import { handleErrorResponse, parseJSON } from 'utils/general';
import MKTypography from 'components/MaterialKit/MKTypography';
import { deleteDatum, getData } from 'api/general';
import { useAuth } from 'contexts/auth';
import Image from 'components/Image';
import moment from 'moment';
import { DATE_FORMAT_DATE_ONLY_DEFAULT, datetimeFormatter } from 'utils/datetime';
import { useNavigate } from 'react-router-dom';

const tableColumns = [
  {
    field: 'alert',
    label: 'Alert',
  },
  {
    field: 'contact/contact_name',
    label: 'Contact Name',
    renderContent: (alert) => {
      const { contact } = alert;
      const { contact_name } = contact;
      return contact_name;
    },
  },
  {
    field: 'contact/phone',
    label: 'Phone (Whatsapp)',
    renderContent: (alert) => {
      const { contact } = alert;
      const { phone } = contact;
      return phone;
    },
  },
  {
    field: 'contact/country',
    label: 'Country',
    renderContent: (alert) => {
      const { contact } = alert;
      const { country } = contact;
      return country;
    },
  },
  {
    field: 'lastmoddate',
    label: 'Updated Date',
    formatter: (value) => {
      return datetimeFormatter(value, 'YYYY-MM-DD');
    },
  },
];

const AlertTableSection = ({
  title_font_size,
  title_color,
  table_name,
  id_field,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  page_options,
  extra_params,
  add_data_path,
  edit_data_path,
  addable,
  editable,
  deletable,
  container_props,
  ...props
}) => {
  const { setAuth } = useAuth();
  const [rowsData, setRowsData] = useState([]);

  const [pageSize, setPageSize] = useState(page_options?.length ? page_options[0] : 10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const navigate = useNavigate();

  const fetchConfigurationsFromApi = useCallback(() => {
    const dataParams = {
      ...extra_params,
      $orderBy: 'lastmoddate desc',
      $skip: currentPage * pageSize,
      $top: pageSize,
    };
    return getData(`/${table_name}`, dataParams, 'bearer')
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setRowsData(data);
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [currentPage, extra_params, pageSize, setAuth, table_name]);

  useEffect(() => {
    if (table_name) {
      fetchConfigurationsFromApi();
    }
  }, [fetchConfigurationsFromApi, table_name]);

  const onClickEdit = useCallback((dataId) => {
    if (edit_data_path) {
      navigate(`${edit_data_path}?${id_field}=${dataId}`);
    }
  }, [id_field, edit_data_path, navigate]);

  return (
    <MKBox
      sx={{
        ...parseJSON(container_props, {}),
      }}
      {...props}
    >
      <MKBox mb={2} display="flex" alignItems="center">
        <MKTypography mr="1rem" color={title_color} fontSize={title_font_size}>
          Alerts
        </MKTypography>
      </MKBox>
      <DataTable
        data={rowsData}
        columns={tableColumns}
        headerBackgroundColor={header_background_color}
        headerFontColor={header_font_color}
        oddRowBackgroundColor={odd_row_background_color}
        oddRowFontColor={odd_row_font_color}
        evenRowBackgroundColor={even_row_background_color}
        evenRowFontColor={even_row_font_color}
        idField={id_field}
        onClickRow={onClickEdit}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={page_options}
        totalCount={totalRows}
        onRowsPerPageChange={setPageSize}
      />
    </MKBox>
  );
};

AlertTableSection.propTypes = {
  title: PropTypes.string,
  title_font_size: PropTypes.string,
  title_color: PropTypes.string,
  table_name: PropTypes.string,
  id_field: PropTypes.string,
  odd_row_background_color: PropTypes.string,
  even_row_background_color: PropTypes.string,
  odd_row_font_color: PropTypes.string,
  even_row_font_color: PropTypes.string,
  header_background_color: PropTypes.string,
  header_font_color: PropTypes.string,
  page_options: PropTypes.array,
  extra_params: PropTypes.object,
  container_props: PropTypes.string,
  add_data_path: PropTypes.string,
  edit_data_path: PropTypes.string,
  columns: PropTypes.array.isRequired,
  addable: PropTypes.bool,
  editable: PropTypes.bool,
  deletable: PropTypes.bool,
};

AlertTableSection.defaultProps = {
  title: '',
  title_font_size: '',
  title_color: '',
  table_name: '',
  id_field: '',
  odd_row_background_color: '',
  even_row_background_color: '',
  odd_row_font_color: '',
  even_row_font_color: '',
  header_background_color: '',
  header_font_color: '',
  page_options: [],
  extra_params: {},
  container_props: '',
  add_data_path: '',
  edit_data_path: '',
  addable: true,
  editable: true,
  deletable: true,
};

export default AlertTableSection;
